/**
 * Lazy modal loader.
 *
 * Some modals drag in heavy assets (Pulpoar SDK, auth forms/scripts) that were
 * previously rendered into the DOM on every page. Instead they are now fetched
 * on the FIRST open via the Section Rendering API (GET /?section_id=<file>),
 * injected, and handed off to the theme's existing modal system.
 *
 * The theme opens modals two ways, both ending at Modal._openModal(
 * getElementById(aria-controls)) — a click on [data-action="open-modal"] and a
 * programmatic so.modal:open event. If the element is not in the DOM yet,
 * _openModal bails, so we intercept BOTH entry points in the capture phase,
 * inject the section, then re-dispatch the open.
 */
(function () {
  const REGISTRY = {
    // aria-controls (modal id)  ->  section filename for the Section Rendering API
    'modal-skin-ai': 'modal-skin-ai',
    'modal-user_auth': 'modal-user-auth',
  };

  const pending = new Map();

  // Scripts inserted via innerHTML are inert — recreate them so they execute.
  function activateScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      const fresh = document.createElement('script');
      [...old.attributes].forEach((attr) => fresh.setAttribute(attr.name, attr.value));
      if (old.src) {
        fresh.async = false; // preserve execution order (e.g. SDK before experience JS)
      } else {
        fresh.textContent = old.textContent;
      }
      old.replaceWith(fresh);
    });
  }

  function loadModal(modalId) {
    if (document.getElementById(modalId)) return Promise.resolve(true);
    if (pending.has(modalId)) return pending.get(modalId);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}?section_id=${REGISTRY[modalId]}`;

    const request = fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const holder = document.createElement('div');
        holder.innerHTML = html.trim();
        const node = holder.firstElementChild; // <div id="shopify-section-…">
        if (!node) return false;
        document.body.appendChild(node);
        activateScripts(node);
        return !!document.getElementById(modalId);
      })
      .catch(() => false)
      .finally(() => pending.delete(modalId));

    pending.set(modalId, request);
    return request;
  }

  function reopen(target) {
    window.SimpleCore._helpers.triggerEvent('modal:open', { target: target });
  }

  function shouldIntercept(modalId) {
    return !!REGISTRY[modalId] && !document.getElementById(modalId);
  }

  // Path 1 — click on a trigger. Capture phase so we run before theme.js's delegate.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-action="open-modal"]');
      if (!trigger) return;
      const modalId = trigger.getAttribute('aria-controls');
      if (!shouldIntercept(modalId)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      loadModal(modalId).then((ok) => ok && reopen(trigger));
    },
    true
  );

  // Path 2 — programmatic so.modal:open. Capture phase so we run before theme.js's
  // listener. After injection we re-dispatch; the guard then lets it fall through.
  document.addEventListener(
    'so.modal:open',
    (event) => {
      const target = event.detail && event.detail.target;
      if (!target) return;
      const modalId = target.getAttribute('aria-controls');
      if (!shouldIntercept(modalId)) return;

      event.stopImmediatePropagation();
      loadModal(modalId).then((ok) => ok && reopen(target));
    },
    true
  );
})();
